import { useEffect } from 'react';

/**
 * Options for configuring useExponentialBackoff.
 */
interface Options {
    /**
     * The initial delay in milliseconds, defaults to `1000`.
     */
    readonly initialDelay?: number;

    /**
     * The maximum delay in milliseconds, defaults to `initialDelay * 10`.
     */
    readonly maxDelay?: number;

    /**
     * The backoff factor which is used to increase the delay from one call to
     * another. Defaults to `1.4`.
     */
    readonly backoffFactor?: number;
}

/**
 * Continuously calls the given callback, with a computed delay between
 * subsequent calls.
 *
 * The first call will be delayed by the given initial delay.
 *
 * If a call is successful (returns true), the following call will also be
 * delayed by the given initial delay.
 *
 * If a call is not successful (returns anything but true or throws an error),
 * the following call will be delayed by the delay of the current call
 * multiplied by the given backoff factor, but at most the given maximum delay.
 */
export function useExponentialBackoff(
    callback: () => boolean | Promise<unknown>,
    { initialDelay = 1000, maxDelay = initialDelay * 10, backoffFactor = 1.4 }: Options
) {
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | null = null;

        const loop = (delay: number) => {
            if (timeout) {
                clearTimeout(timeout);
            }

            const increasedDelay = Math.min(delay * backoffFactor, maxDelay);

            timeout = setTimeout(async () => {
                try {
                    const changed = await callback();

                    if (changed === true) {
                        loop(initialDelay);
                    } else {
                        loop(increasedDelay);
                    }
                } catch (err) {
                    loop(increasedDelay);
                    throw err;
                }
            }, delay);
        };

        loop(initialDelay);

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [callback, initialDelay, maxDelay, backoffFactor]);
}
